import AxiosProvider from '@/app/utils/api/axios-provider';
import { postSseRequest, readSseStream } from '@/app/utils/api/sse-stream';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { PaginationParams } from '@/app/shared/types/params/request-params.interface';
import { ImportUploadResponse } from '@/app/shared/types/models/import-upload.interface';
import { ApiResponse } from '@/app/shared/types/api/api-response.interface';
import { ImportCompletionRequest } from '@/app/shared/types/models/import-complete.interface';

export interface ImportProgressEvent {
  step: string;
  current: number;
  total: number;
  message: string;
}

export interface DeploymentProgressEvent {
  step: string;
  current: number;
  total: number;
  message: string;
}

export interface DeploymentCompleteEvent {
  step: string;
  downloadToken: string;
  fileName: string;
  message: string;
}

export default {
  getSystems(clientId: number, queryParams: PaginationParams) {
    return AxiosProvider.amsApiClient.get(`clients/${clientId}/systems`, {
      params: { ...queryParams },
    });
  },
  getSystemById(clientId: number, systemId: number): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.get(
      `/clients/${clientId}/systems/${systemId}`
    );
  },
  getSystemAttributesList(
    clientId: number,
    systemId: number
  ): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.get(
      `/clients/${clientId}/systems/${systemId}/attributes`
    );
  },
  createSystem(clientId: number, systemData: any): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.post(
      `clients/${clientId}/systems`,
      systemData
    );
  },
  deleteSystemById(clientId: number, systemId: number): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.delete(
      `/clients/${clientId}/systems/${systemId}`
    );
  },
  copySystemById(
    clientId: number,
    copyFromSystemId: number,
    systemData: { name: string; notes: string; createdBy: string }
  ): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.post(
      `clients/${clientId}/systems/${copyFromSystemId}/copy`,
      systemData
    );
  },
  updateSystem(
    clientId: number,
    systemId: number,
    system: any
  ): Promise<AxiosResponse> {
    return AxiosProvider.amsApiClient.patch(
      `/clients/${clientId}/systems/${systemId}`,
      system
    );
  },
  downloadDeploymentFile(
    clientId: number,
    deploymentInfo: any
  ): Promise<AxiosResponse> {
    const config: AxiosRequestConfig = {
      responseType: 'blob',
    };
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/systems/deployment`,
      deploymentInfo,
      config
    );
  },
  async streamDeploymentFile(
    clientId: number,
    deploymentInfo: unknown,
    onProgress?: (_event: DeploymentProgressEvent) => void
  ): Promise<{ blob: Blob; fileName: string }> {
    const baseURL = (config.VUE_APP_ATB_AMS_SERVICE_URL || '').replace(
      /[\\/]+$/,
      ''
    );
    const streamUrl = `${baseURL}/clients/${clientId}/systems/deployment/stream`;

    const response = await postSseRequest(
      streamUrl,
      deploymentInfo,
      'Deployment failed'
    );

    const completeEvent = await readSseStream<
      DeploymentProgressEvent,
      DeploymentCompleteEvent
    >(response, onProgress, 'Deployment failed');

    if (!completeEvent) {
      throw new Error('Deployment stream ended without completion event');
    }

    const downloadResponse = await AxiosProvider.amsApiClient.get(
      `/clients/${clientId}/systems/deployment/download/${completeEvent.downloadToken}`,
      { responseType: 'blob' }
    );

    return {
      blob: new Blob([downloadResponse.data], { type: 'application/zip' }),
      fileName: completeEvent.fileName,
    };
  },
  async validateImportFile(
    clientId: number,
    formData: FormData
  ): Promise<AxiosResponse<ApiResponse<ImportUploadResponse>>> {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/systems/import/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  async completeImportFileData(
    clientId: number,
    importData: ImportCompletionRequest,
    onProgress?: (_event: ImportProgressEvent) => void
  ): Promise<void> {
    const baseURL = (config.VUE_APP_ATB_AMS_SERVICE_URL || '').replace(
      /[\\/]+$/,
      ''
    );
    const url = `${baseURL}/clients/${clientId}/systems/import/complete`;

    const response = await postSseRequest(url, importData, 'Import failed');
    await readSseStream<ImportProgressEvent>(response, onProgress, 'Import failed');
  },
  async getProtectedAttributes(clientId: number, systemId: number) {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/systems/${systemId}/publish/protected/attributes`
    );
  },
  async exportData(
    clientId: number,
    systemId: number,
    payload: { datasources: string[]; publishedAttributes: string[] }
  ) {
    return AxiosProvider.amsApiClient.post(
      `/clients/${clientId}/systems/${systemId}/export/system`,
      payload
    );
  },
};
